import React, { useRef, useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Separator } from './ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Switch } from './ui/switch';
import { Avatar, AvatarFallback } from './ui/avatar';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from './ui/alert-dialog';
import { User, Mail, Phone, MapPin, Lock, Building, Bell, Shield, LogOut, Eye, EyeOff, Save, AlertTriangle, Map } from 'lucide-react';
import { toast } from 'sonner';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { ShiftTiming } from '@/redux/slices/auth/authSlice';
// import axiosInstance from '@/api/axiosInstance';
import axios from 'axios';
// import { loginSuccess } from '@/redux/slices/auth/authSlice';
// import { jwtDecode, JwtPayload } from 'jwt-decode';
import { changePassword, updateImage, updatePharmacy, updateUserProfile, userNotificationSettings } from '@/redux/slices/auth/authSlice';
import { logout } from '@/redux/slices/auth/authSlice';
import axiosInstance from '@/api/axiosInstance';
import { LocationPickerModal } from './LocationPickerModal';
import GlobalLoader from './ui/GlobalLoader';

interface SettingsProps {
  onLogout: () => void;
}

interface UserProfile {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  title: string;
}

interface PharmacyInfo {
  name: string;
  location: {
    latitude: number;
    longitude: number;
  address: string;
  } | string;
  licenseNumber: string;
  openingTime: string;
  closingTime: string;
  shiftTimings: ShiftTiming[];
}

interface NotificationSettings {
  orderUpdates: boolean;
  pickupRequests: boolean;
  deliveryAlerts: boolean;
  systemMaintenance: boolean;
}

export function Settings({ onLogout }: SettingsProps) {
  // const [userProfile, setUserProfile] = useState<UserProfile>({
  //   firstName: 'Sarah',
  //   lastName: 'Wilson',
  //   email: 'sarah.wilson@centralpharmacy.com',
  //   phone: '(555) 123-4567',
  //   title: 'PharmD'
  // });
  const baseURL = import.meta.env.VITE_API_BASE_URL;
  const user = useAppSelector((state) => state.auth.user);

  const dispatch = useAppDispatch();

  const [selectedImage, setSelectedImage] = useState<string | null>(user?.imageURL || null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const openInput = () => fileInputRef.current?.click();

  const [userProfile, setUserProfile] = useState({
    firstName: user?.firstName,
    lastName: user?.lastName,
    email: user?.email,
    phone: user?.phone,
    title: user?.professionalTitle,
  });

  // const [pharmacyInfo, setPharmacyInfo] = useState<PharmacyInfo>({
  //   name: 'Central Pharmacy',
  //   address: '123 Medical Center Dr',
  //   city: 'New York',
  //   state: 'NY',
  //   zipCode: '10001',
  //   licenseNumber: 'NY-PH-12345'
  // });

  // Helper function to transform GeoJSON to frontend format
  const transformLocation = (location: any) => {
    if (!location || typeof location === 'string') return location;
    
    // If already in frontend format (has latitude property)
    if (location.latitude !== undefined) {
      return location;
    }
    
    // If in GeoJSON format (has coordinates array)
    if (location.coordinates && Array.isArray(location.coordinates)) {
      return {
        latitude: location.coordinates[1],  // Second element is latitude
        longitude: location.coordinates[0], // First element is longitude
        address: location.address || ''
      };
    }
    
    return location;
  };

  const [pharmacyInfo, setPharmacyInfo] = useState<PharmacyInfo>({
    name: user?.pharmacyName || '',
    openingTime: user?.openingTime || '',
    closingTime: user?.closingTime || '',
    shiftTimings: user?.shiftTimings || [
      { day: 'Monday', isOpen: true, openingTime: '11:00', closingTime: '19:00' },
      { day: 'Tuesday', isOpen: true, openingTime: '11:00', closingTime: '19:00' },
      { day: 'Wednesday', isOpen: true, openingTime: '11:00', closingTime: '19:00' },
      { day: 'Thursday', isOpen: true, openingTime: '11:00', closingTime: '19:00' },
      { day: 'Friday', isOpen: true, openingTime: '11:00', closingTime: '19:00' },
      { day: 'Saturday', isOpen: true, openingTime: '10:00', closingTime: '18:00' },
      { day: 'Sunday', isOpen: false, openingTime: '00:00', closingTime: '00:00' }
    ],
    location: transformLocation((user as any)?.location) || '',
    licenseNumber: user?.licenseNumber || '',
  });

  const [isMapModalOpen, setIsMapModalOpen] = useState(false);
  const [selectedPosition, setSelectedPosition] = useState<[number, number] | null>(() => {
    const userLocation = transformLocation((user as any)?.location);
    if (userLocation && typeof userLocation !== 'string' && userLocation.latitude) {
      return [userLocation.latitude, userLocation.longitude];
    }
    return null;
  });
  const [tempAddress, setTempAddress] = useState(() => {
    const userLocation = transformLocation((user as any)?.location);
    return userLocation && typeof userLocation !== 'string' ? userLocation.address : '';
  });

  // Sync pharmacy info when user data changes
  useEffect(() => {
    if (user) {
      const rawLocation = (user as any)?.location;
      const transformedLocation = transformLocation(rawLocation);
      
      console.log('=== LOCATION DEBUG ===');
      console.log('User object:', user);
      console.log('Raw location from backend:', rawLocation);
      console.log('Transformed location:', transformedLocation);
      console.log('Location type:', typeof transformedLocation);
      
      if (transformedLocation) {
        console.log('Location is truthy');
        if (typeof transformedLocation !== 'string') {
          console.log('Location latitude:', transformedLocation.latitude);
          console.log('Location longitude:', transformedLocation.longitude);
          console.log('Location address:', transformedLocation.address);
        }
      } else {
        console.log('Location is falsy/undefined');
      }
      console.log('======================');
      
      setPharmacyInfo(prev => ({
        ...prev,
        name: user.pharmacyName || prev.name,
        location: transformedLocation || prev.location,
        licenseNumber: user.licenseNumber || prev.licenseNumber,
      }));
      
      // Update selected position if location exists
      if (transformedLocation && typeof transformedLocation !== 'string' && transformedLocation.latitude) {
        setSelectedPosition([transformedLocation.latitude, transformedLocation.longitude]);
        setTempAddress(transformedLocation.address || '');
      }
    }
  }, [user]);

  // const [notificationSettings, setNotificationSettings] =
  //   useState<NotificationSettings>({
  //     orderUpdates: true,
  //     pickupRequests: true,
  //     deliveryAlerts: false,
  //     systemMaintenance: false,
  //   });

  const [notificationSettings, setNotificationSettings] = useState(user.notificationSettings);

  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  });

  const [isLoading, setIsLoading] = useState(false);

  const handleUserProfileUpdate = async (e: React.FormEvent) => {
    e.preventDefault();

    // Simulate API call
    // setTimeout(() => {
    //   setIsLoading(false);
    //   toast.success('Profile updated successfully');
    // }, 1000);

    try {
      setIsLoading(true);

      // const response = await axiosInstance.post(`${baseURL}/user/profile`, userProfile);
      
      // console.log(response.data.data.user);
      // toast.success(response.data.message);
      const res = await dispatch<any>(updateUserProfile(userProfile)).unwrap();
    } catch (error) {
      if (axios.isAxiosError && error.response) {
        console.log(error.response.data?.message);
        toast.error(error.response.data?.message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handlePharmacyUpdate = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      setIsLoading(true);
      
      // Send as is - backend will handle GeoJSON transformation
      console.log('Sending to backend:', pharmacyInfo);
      const res = await dispatch<any>(updatePharmacy(pharmacyInfo)).unwrap();
      
    } catch (error) {
      if (axios.isAxiosError && error.response) {
        console.log(error.response.data?.message);
        toast.error(error.response.data?.message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleMapModalOpen = () => {
    setIsMapModalOpen(true);
  };

  const handleLocationConfirm = (location: { latitude: number; longitude: number; address: string }) => {
    setPharmacyInfo(prev => ({ ...prev, location }));
    setSelectedPosition([location.latitude, location.longitude]);
    setTempAddress(location.address);
  };

  const handleShiftTimingChange = (day: string, field: 'isOpen' | 'openingTime' | 'closingTime', value: boolean | string) => {
    setPharmacyInfo(prev => ({
      ...prev,
      shiftTimings: prev.shiftTimings.map(timing => 
        timing.day === day 
          ? { ...timing, [field]: value }
          : timing
      )
    }));
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error("New passwords do not match");
      return;
    }

    if (passwordData.newPassword.length < 8) {
      toast.error("Password must be at least 8 characters long");
      return;
    }

    // setIsLoading(true);

    // Simulate API call
    // setTimeout(() => {
    //   setIsLoading(false);
    //   setPasswordData({
    //     currentPassword: '',
    //     newPassword: '',
    //     confirmPassword: ''
    //   });
    //   toast.success('Password updated successfully');
    // }, 1000);

    try {
      setIsLoading(true);
      // const response = await axiosInstance.post(`${baseURL}/user/change-password`, passwordData);
      const res = await dispatch<any>(changePassword(passwordData)).unwrap();
      setPasswordData({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
      // toast.success(response.data.message);
    } catch (error) {
      if (axios.isAxiosError && error.response) {
        console.log(error.response.data?.message);
        toast.error(error.response.data?.message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleNotificationUpdate = async(
    key: keyof NotificationSettings,
    value: boolean
  ) => {
    
    setNotificationSettings((prev) => ({ ...prev, [key]: value }));
    // toast.success("Notification preferences updated");
    
    // const res = await axiosInstance.post(`http://localhost:5000/api/user/notification-settings`, {key, value});
    await dispatch(userNotificationSettings({key, value})).unwrap();
  };

  const confirmLogout = () => {
    // onLogout();
    dispatch(logout());
    // toast.success("Logged out successfully");
  };

  const handleImageUpload = async (file: File) => {
    const formData = new FormData();
    formData.append("image", file);

    try {
      setIsLoading(true);
      const res = await dispatch<any>(updateImage(formData));
      // const response = await axiosInstance.post(`${baseURL}/user/upload-image`, formData, {
      //   headers: { "Content-Type": "multipart/form-data" },
      // });

      // if (response.status === 200) {
      //   // setSelectedImage(response.data.imageURL);
      //   // const token = response.data.token;
      //   // const decoded = jwtDecode<JwtPayload>(token);
      //   // dispatch(updateUserState({token, decoded}));
      //   toast.success(response.data.message);
      // }
    } catch (error) {
      if (axios.isAxiosError && error.response) {
        toast.error(error.response.data?.message);
        console.log(error);
      }
    } finally {
      setIsLoading(false);
    }
  };
                  console.error(pharmacyInfo.location)

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        {/* <h1 className="text-2xl font-semibold text-gray-900">Settings</h1> */}
        <p className="text-gray-600">
          Manage your account settings
        </p>
      </div>
      <div>
      </div>
      <Tabs defaultValue="profile" className="space-y-6">
        <TabsList className="grid w-full grid-cols-3 bg-gray-100 p-1 rounded-lg">
          <TabsTrigger
            value="profile"
            className="data-[state=active]:bg-white data-[state=active]:text-green-700 data-[state=active]:shadow-sm data-[state=active]:border data-[state=active]:border-green-200 transition-all duration-200 hover:bg-gray-50"
          >
            Profile
          </TabsTrigger>
          <TabsTrigger
            value="security"
            className="data-[state=active]:bg-white data-[state=active]:text-green-700 data-[state=active]:shadow-sm data-[state=active]:border data-[state=active]:border-green-200 transition-all duration-200 hover:bg-gray-50"
          >
            Security
          </TabsTrigger>
          {user?.role === "pharmacy" ? (
            <TabsTrigger
              value="pharmacy"
              className="data-[state=active]:bg-white data-[state=active]:text-green-700 data-[state=active]:shadow-sm data-[state=active]:border data-[state=active]:border-green-200 transition-all duration-200 hover:bg-gray-50"
            >
              Pharmacy
            </TabsTrigger>
          ) : null}

          <TabsTrigger
            value="notifications"
            className="data-[state=active]:bg-white data-[state=active]:text-green-700 data-[state=active]:shadow-sm data-[state=active]:border data-[state=active]:border-green-200 transition-all duration-200 hover:bg-gray-50"
          >
            Notifications
          </TabsTrigger>
        </TabsList>

        {/* Profile Tab */}
        <TabsContent value="profile">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <User className="w-5 h-5" />
                <span>Personal Information</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleUserProfileUpdate} className="space-y-6">
                {/* Profile Picture */}
                <div className="flex items-center space-x-4">
                  {user?.imageURL ? (
                    <div>
                      <Avatar
                        onClick={openInput}
                        className="w-20 h-20 cursor-pointer"
                      >
                        <AvatarFallback className="bg-green-100 text-green-700 text-lg">
                          <img src={user?.imageURL} alt="profile picture" />
                        </AvatarFallback>
                      </Avatar>
                      {/* <img
                  src={selectedImage ?? ""}
                  alt="Profile"
                  className="w-40 h-40 border-3 border-gray-300 p-2 rounded-full cursor-pointer object-cover"
                  onClick={openInput}
                /> */}
                      <input
                        type="file"
                        ref={fileInputRef}
                        accept="image/png, image/jpeg, image/jpg"
                        hidden
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) {
                            setImageFile(file);
                            const imageUrl = URL.createObjectURL(file);
                            setSelectedImage(imageUrl);
                            handleImageUpload(file);
                          }
                        }}
                      />
                    </div>
                  ) : (
                    <Avatar className="w-20 h-20">
                      <AvatarFallback className="bg-green-100 text-green-700 text-lg">
                        {userProfile.firstName.charAt(0)}
                        {userProfile.lastName.charAt(0)}
                      </AvatarFallback>
                    </Avatar>
                  )}
                  <div>
                    <Button onClick={openInput} variant="outline" type="button">
                      Change Photo
                    </Button>
                    <p className="text-sm text-gray-500 mt-1">
                      JPG, PNG or GIF. Max size 2MB.
                    </p>
                  </div>
                </div>

                <Separator />

                {/* Personal Details */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="firstName">
                      {user.isAdmin ? "Name" : "First Name"}
                    </Label>
                    <Input
                      id="firstName"
                      value={userProfile.firstName}
                      onChange={(e) =>
                        setUserProfile((prev) => ({
                          ...prev,
                          firstName: e.target.value,
                        }))
                      }
                      required
                    />
                  </div>
                  {!user.isAdmin ? (
                    <>
                      <div className="space-y-2">
                        <Label htmlFor="lastName">Last Name</Label>
                        <Input
                          id="lastName"
                          value={userProfile.lastName}
                          onChange={(e) =>
                            setUserProfile((prev) => ({
                              ...prev,
                              lastName: e.target.value,
                            }))
                          }
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="title">Professional Title</Label>
                        <Select
                          value={userProfile.title}
                          onValueChange={(value: any) =>
                            setUserProfile((prev) => ({ ...prev, title: value }))
                          }
                        >
                          <SelectTrigger className="bg-white">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-white">
                            <SelectItem value="PharmD">PharmD</SelectItem>
                            <SelectItem value="RPh">RPh</SelectItem>
                            <SelectItem value="PharmTech">
                              Pharmacy Technician
                            </SelectItem>
                            <SelectItem value="Manager">
                              Pharmacy Manager
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </>
                  ) : null}

                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone Number</Label>
                    <Input
                      id="phone"
                      type="tel"
                      value={userProfile.phone}
                      onChange={(e) =>
                        setUserProfile((prev) => ({
                          ...prev,
                          phone: e.target.value,
                        }))
                      }
                      required
                    />
                  </div>

                  <div className="space-y-2 md:col-span-2">
                    <Label htmlFor="email">Email Address</Label>
                    <div className="flex items-center space-x-2">
                      <Mail className="w-4 h-4 text-gray-400" />
                      <Input
                        id="email"
                        type="email"
                        value={userProfile.email}
                        // onChange={(e) => setUserProfile(prev => ({ ...prev, email: e.target.value }))}
                        disabled
                        readOnly
                        className="flex-1 disabled cursor-default read-only:border-none"
                      />
                    </div>
                  </div>
                </div>

                <div className="flex justify-end">
                  <Button
                    type="submit"
                    disabled={isLoading}
                    className="bg-green-600 text-white font bold hover:bg-green-700"
                  >
                    <Save className="!w-4 !h-4 mr-2 text-white" />
                    {isLoading ? "Saving..." : "Save Changes"}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Security Tab */}
        <TabsContent value="security">
          <div className="space-y-6">
            {/* Password Change */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Lock className="w-5 h-5" />
                  <span>Change Password</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handlePasswordChange} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="currentPassword">Current Password</Label>
                    <div className="relative">
                      <Input
                        id="currentPassword"
                        type={showPasswords.current ? "text" : "password"}
                        value={passwordData.currentPassword}
                        onChange={(e) =>
                          setPasswordData((prev) => ({
                            ...prev,
                            currentPassword: e.target.value,
                          }))
                        }
                        required
                        className="pr-10"
                      />
                      <button
                        type="button"
                        onClick={() =>
                          setShowPasswords((prev) => ({
                            ...prev,
                            current: !prev.current,
                          }))
                        }
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500"
                      >
                        {showPasswords.current ? (
                          <EyeOff className="w-4 h-4" />
                        ) : (
                          <Eye className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="newPassword">New Password</Label>
                    <div className="relative">
                      <Input
                        id="newPassword"
                        type={showPasswords.new ? "text" : "password"}
                        value={passwordData.newPassword}
                        onChange={(e) =>
                          setPasswordData((prev) => ({
                            ...prev,
                            newPassword: e.target.value,
                          }))
                        }
                        required
                        className="pr-10"
                      />
                      <button
                        type="button"
                        onClick={() =>
                          setShowPasswords((prev) => ({
                            ...prev,
                            new: !prev.new,
                          }))
                        }
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500"
                      >
                        {showPasswords.new ? (
                          <EyeOff className="w-4 h-4" />
                        ) : (
                          <Eye className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                    <p className="text-sm text-gray-500">
                      Must be at least 8 characters long
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="confirmPassword">
                      Confirm New Password
                    </Label>
                    <div className="relative">
                      <Input
                        id="confirmPassword"
                        type={showPasswords.confirm ? "text" : "password"}
                        value={passwordData.confirmPassword}
                        onChange={(e) =>
                          setPasswordData((prev) => ({
                            ...prev,
                            confirmPassword: e.target.value,
                          }))
                        }
                        required
                        className="pr-10"
                      />
                      <button
                        type="button"
                        onClick={() =>
                          setShowPasswords((prev) => ({
                            ...prev,
                            confirm: !prev.confirm,
                          }))
                        }
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500"
                      >
                        {showPasswords.confirm ? (
                          <EyeOff className="w-4 h-4" />
                        ) : (
                          <Eye className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                  </div>

                  <div className="flex justify-end">
                    <Button
                      type="submit"
                      disabled={isLoading}
                      className="bg-green-600 hover:bg-green-700 text-white"
                    >
                      <Shield className="w-4 h-4 mr-2" />
                      {isLoading ? "Updating..." : "Update Password"}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>

            {/* Account Actions */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <AlertTriangle className="w-5 h-5" />
                  <span>Account Actions</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-red-50 rounded-lg">
                    <div>
                      <h3 className="font-medium text-red-900">Sign Out</h3>
                      <p className="text-sm text-red-700">
                        Sign out of your account on this device
                      </p>
                    </div>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="destructive">
                          <LogOut className="w-4 h-4 mr-2" />
                          Sign Out
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>
                            Are you sure you want to sign out?
                          </AlertDialogTitle>
                          <AlertDialogDescription>
                            You will be logged out of your account and
                            redirected to the login page.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={confirmLogout}
                            className="bg-red-600 hover:bg-red-700"
                          >
                            Sign Out
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Pharmacy Tab */}
        <TabsContent value="pharmacy">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Building className="w-5 h-5" />
                <span>Pharmacy Information</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handlePharmacyUpdate} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2 md:col-span-2">
                    <Label htmlFor="pharmacyName">Pharmacy Name</Label>
                    <Input
                      id="pharmacyName"
                      value={pharmacyInfo.name}
                      onChange={(e) =>
                        setPharmacyInfo((prev) => ({
                          ...prev,
                          name: e.target.value,
                        }))
                      }
                      required
                    />
                  </div>

                  {/* Shift Timings */}
                  <div className="space-y-4">
                    <Label className="text-base font-medium">Operating Hours</Label>
                    <p className="text-sm text-gray-600">Set your pharmacy's operating hours for each day of the week</p>
                    <div className="space-y-3">
                      {pharmacyInfo.shiftTimings.map((timing) => (
                        <div key={timing.day} className="flex items-center space-x-4 p-3 border border-gray-200 rounded-lg">
                          <div className="w-20 text-sm font-medium text-gray-700">
                            {timing.day}
                          </div>
                          
                          <div className="flex items-center space-x-2">
                            <input
                              type="checkbox"
                              id={`${timing.day}-open`}
                              checked={timing.isOpen}
                              onChange={(e) => handleShiftTimingChange(timing.day, 'isOpen', e.target.checked)}
                              className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                            />
                            <label htmlFor={`${timing.day}-open`} className="text-sm text-gray-700">
                              Open
                            </label>
                          </div>

                          {timing.isOpen ? (
                            <div className="flex items-center space-x-2">
                              <Input
                                type="time"
                                value={timing.openingTime}
                                onChange={(e) => handleShiftTimingChange(timing.day, 'openingTime', e.target.value)}
                                className="h-8 w-32 text-sm"
                              />
                              <span className="text-sm text-gray-500">to</span>
                              <Input
                                type="time"
                                value={timing.closingTime}
                                onChange={(e) => handleShiftTimingChange(timing.day, 'closingTime', e.target.value)}
                                className="h-8 w-32 text-sm"
                              />
                            </div>
                          ) : (
                            <span className="text-sm text-gray-500 font-medium">Closed</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="space-y-2 md:col-span-2">
                    <Label htmlFor="location">Pharmacy Location</Label>
                    <div className="flex gap-2">
                    <Input
                        id="location"
                        value={
                          typeof pharmacyInfo.location === 'string' 
                            ? pharmacyInfo.location 
                            : pharmacyInfo.location?.address || ''
                        }
                        placeholder="Click map icon to select location"
                        readOnly
                      required
                        className="flex-1"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          if (typeof pharmacyInfo.location !== 'string' && pharmacyInfo.location?.latitude) {
                            setSelectedPosition([pharmacyInfo.location.latitude, pharmacyInfo.location.longitude]);
                            setTempAddress(pharmacyInfo.location.address);
                          } else {
                            setSelectedPosition(null);
                            setTempAddress('');
                          }
                          handleMapModalOpen();
                        }}
                        className="shrink-0"
                      >
                        <Map className="w-4 h-4 mr-2" />
                        Select on Map
                      </Button>
                  </div>
                    {typeof pharmacyInfo.location !== 'string' && pharmacyInfo.location?.latitude && (
                      <p className="text-xs text-gray-500 mt-1">
                        📍 Coordinates: {pharmacyInfo.location.latitude.toFixed(6)}, {pharmacyInfo.location.longitude.toFixed(6)}
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="licenseNumber">License Number</Label>
                    <Input
                      id="licenseNumber"
                      value={pharmacyInfo.licenseNumber}
                      onChange={(e) =>
                        setPharmacyInfo((prev) => ({
                          ...prev,
                          licenseNumber: e.target.value,
                        }))
                      }
                      // required
                    />
                  </div>
                </div>

                <div className="flex justify-end">
                  <Button
                    type="submit"
                    disabled={isLoading}
                    className="bg-green-600 hover:bg-green-700 text-white"
                  >
                    <Save className="w-4 h-4 mr-2" />
                    {isLoading ? "Saving..." : "Save Changes"}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Notifications Tab */}
        <TabsContent value="notifications">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Bell className="w-5 h-5" />
                <span>Notification Preferences</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-medium">Order Updates</h3>
                    <p className="text-sm text-gray-500">
                      Get notified about order status changes
                    </p>
                  </div>
                  <Switch
                    checked={notificationSettings.orderUpdates}
                    onCheckedChange={(checked: any) =>
                      handleNotificationUpdate("orderUpdates", checked)
                    }
                  />
                </div>

                <Separator />

                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-medium">Pickup Requests</h3>
                    <p className="text-sm text-gray-500">
                      Receive alerts for new pickup requests
                    </p>
                  </div>
                  <Switch
                    checked={notificationSettings.pickupRequests}
                    onCheckedChange={(checked: any) =>
                      handleNotificationUpdate("pickupRequests", checked)
                    }
                  />
                </div>

                <Separator />

                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-medium">Delivery Alerts</h3>
                    <p className="text-sm text-gray-500">
                      Get notified when deliveries are completed
                    </p>
                  </div>
                  <Switch
                    checked={notificationSettings.deliveryAlerts}
                    onCheckedChange={(checked: any) =>
                      handleNotificationUpdate("deliveryAlerts", checked)
                    }
                  />
                </div>

                <Separator />

                {/* <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-medium">System Maintenance</h3>
                    <p className="text-sm text-gray-500">
                      Receive updates about system maintenance
                    </p>
                  </div>
                  <Switch
                    checked={notificationSettings.systemMaintenance}
                    onCheckedChange={(checked: any) =>
                      handleNotificationUpdate("systemMaintenance", checked)
                    }
                  />
                </div> */}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Location Picker Modal */}
      <LocationPickerModal
        isOpen={isMapModalOpen}
        onClose={() => setIsMapModalOpen(false)}
        onConfirm={handleLocationConfirm}
        initialPosition={selectedPosition}
        initialAddress={tempAddress}
      />

      {/* Global loading overlay */}
      <GlobalLoader 
        isLoading={isLoading}
        message="Updating settings..."
        subMessage="Please wait while we save your changes"
      />
    </div>
  );
}
